<template>
  <v-dialog v-model="dialog" max-width="500px">
    <template v-slot:activator="{ props }">
      <v-btn color="primary" dark class="mb-2" v-bind="props">{{ $t('license.addLicense') }}</v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ $t('license.addNewLicense') }}</span>
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="addLicense" ref="form">
          <v-text-field v-model="newLicense.machine_id" :label="$t('license.machineId')" required></v-text-field>
          <v-text-field v-model="newLicense.client_name" :label="$t('license.clientName')" required></v-text-field>
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedDate"
                :label="$t('license.validDate')"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="datePickerValue"
              @update:model-value="updateDate"
            ></v-date-picker>
          </v-menu>
          <v-text-field
            v-model.number="newLicense.friendly_prompt_period"
            :label="$t('license.friendlyPromptPeriod')"
            type="number"
            required
          ></v-text-field>
          <v-text-field
            v-model.number="newLicense.restricted_warning_period"
            :label="$t('license.restrictedWarningPeriod')"
            type="number"
            required
          ></v-text-field>
          <v-text-field
            v-model="newLicense.client_contact"
            :label="$t('license.clientContact')"
          ></v-text-field>
          <v-text-field
            v-model="newLicense.address"
            :label="$t('license.address')"
          ></v-text-field>
          <div class="permissions-tree">
            <div v-for="genre in genreCategories" :key="genre.genre.inner_id" class="genre-item">
              <v-checkbox
                v-model="selectedGenres"
                :label="genre.genre.name"
                :value="genre.genre.inner_id"
                @change="updateCategorySelection(genre)"
              ></v-checkbox>
              <div v-for="category in genre.categories" :key="category.inner_id" class="category-item">
                <v-checkbox
                  v-model="selectedCategories"
                  :label="category.name"
                  :value="category.inner_id"
                  :disabled="!selectedGenres.includes(genre.genre.inner_id)"
                ></v-checkbox>
              </div>
            </div>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="closeDialog">{{ $t('license.cancel') }}</v-btn>
        <v-btn color="blue darken-1" text @click="addLicense" :disabled="!isFormValid">{{ $t('license.add') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import yaml from 'js-yaml'

const emit = defineEmits(['license-added'])

const dialog = ref(false)
const menu = ref(false)
const newLicense = ref({
  machine_id: '',
  client_name: '',
  valid_date: '',
  friendly_prompt_period: 7,
  restricted_warning_period: 0,
  client_contact: '',
  address: ''
})
const formattedDate = ref('')
const datePickerValue = ref(null)
const genreCategories = ref([])
const selectedGenres = ref([])
const selectedCategories = ref([])

const isFormValid = computed(() => {
  return newLicense.value.machine_id &&
         newLicense.value.client_name &&
         newLicense.value.valid_date &&
         newLicense.value.friendly_prompt_period >= 0 &&
         newLicense.value.restricted_warning_period >= 0
})

onMounted(async () => {
  await fetchGenreCategories()
})

async function fetchGenreCategories() {
  try {
    const response = await axios.get('/api/genre-category-yaml', {
      responseType: 'text'
    })
    const yamlData = response.data
    const jsonData = yaml.load(yamlData)
    genreCategories.value = jsonData
  } catch (error) {
    console.error('Error fetching genre categories:', error)
  }
}

function updateCategorySelection(genre) {
  const genreId = genre.genre.inner_id
  if (selectedGenres.value.includes(genreId)) {
    genre.categories.forEach(category => {
      const categoryId = category.inner_id
      if (!selectedCategories.value.includes(categoryId)) {
        selectedCategories.value.push(categoryId)
      }
    })
  } else {
    selectedCategories.value = selectedCategories.value.filter(id =>
      !genre.categories.some(category => category.inner_id === id)
    )
  }
}

function updateDate(newDate) {
  if (!newDate) return
  const date = new Date(newDate)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = '00'
  const minutes = '00'
  const seconds = '00'
  formattedDate.value = `${year}-${month}-${day}`
  newLicense.value.valid_date = `${year}${month}${day}-${hours}${minutes}${seconds}`
  menu.value = false
}

function closeDialog() {
  dialog.value = false
  resetForm()
}

function resetForm() {
  newLicense.value = {
    machine_id: '',
    client_name: '',
    valid_date: '',
    friendly_prompt_period: 7,
    restricted_warning_period: 0,
    client_contact: '',
    address: ''
  }
  formattedDate.value = ''
  datePickerValue.value = null
  selectedGenres.value = []
  selectedCategories.value = []
}

async function addLicense() {
  if (!isFormValid.value) return

  try {
    await axios.post('/api/licenses', {
      ...newLicense.value,
      genres: selectedGenres.value,
      categories: selectedCategories.value
    })
    emit('license-added')
    closeDialog()
  } catch (error) {
    console.error('Error adding license:', error)
  }
}
</script>

<style scoped>
.category-item {
  margin-left: 20px;
}
</style>